/*
Copyright (c) 2012-2022 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
/** @jsx jsx */
import { jsx } from "@emotion/core"
import { FC, Fragment, useCallback, useEffect } from "react"
import { Drawer } from "antd"
import { Button, Form, Input } from "grab-ui"
import { useTranslationsMap } from "common/utils/i18n"
import { useDispatch, useSelector } from "react-redux"
import { actionCreator } from "store/store"
import { AppState } from "store/store.type"
import { HTTPStatus } from "constants/endpoints"
import { Config } from "constants/config"
import { formHasErrors, getDalErrMessage, isMartBusinessType } from "utils/misc"
import {
  menuNameTooLongBEMessage,
  menuNameAlreadyExistBEMessage,
  inputName,
} from "constants/menu_group"
import notification from "components/common/notification"
import { getResponseErrorEventLabels, TroyWebTracker } from "utils/tracking"
import { EventAction, EventCategory } from "constants/tracking"
import { drawerTitleStyle, drawerFooter } from "../styles"
import {
  menuGroupDrawerFormContainer,
  menuGroupDrawerMenuConfirmButton,
  menuGroupDrawerStyle,
} from "./styles"

export interface MenuGroupDrawerProps extends FormComponentProps {
  visible: boolean
  onClose: () => void
}

export const MenuGroupDrawerComponent: FC<MenuGroupDrawerProps> = ({
  visible,
  onClose,
  form,
}: MenuGroupDrawerProps) => {
  const {
    getFieldDecorator,
    getFieldValue,
    validateFields,
    setFieldsValue,
    isFieldsTouched,
    getFieldsError,
    setFields,
  } = form
  const dispatch = useDispatch()
  const {
    merchantGroupID,
    menuGroupDetail: { menuGroupID, name: menuGroupName, businessType },
  } = useSelector((state: AppState) => state.menu)
  const {
    messages: { error: globalErrorMessage },
  } = useSelector((state: AppState) => state.global)
  const { maxLengthMenuGroupName } = Config
  const { menuGroupNameField } = inputName

  const textMap = useTranslationsMap({
    title: isMartBusinessType(businessType)
      ? "menu.menuGroup.editName.forCatalogue.editNameTitle"
      : "menu.menuGroup.editName.forMenu.editNameTitle",
    menuGroupNameLabel: isMartBusinessType(businessType)
      ? "menu.menuGroup.editName.forCatalogue.editNameInputLabel"
      : "menu.menuGroup.editName.forMenu.editNameInputLabel",
    save: "common.save",
    menuNameCharExceeded: "menu.menuGroup.menuName.charLengthExceeded",
    menuNameAlreadyExist: isMartBusinessType(businessType)
      ? "menu.menuGroup.editName.forCatalogue.nameExist"
      : "menu.menuGroup.editName.forMenu.nameExist",
  })

  const handleDrawerClose = useCallback(() => {
    TroyWebTracker.sendEvent({
      category: EventCategory.menuGroupEdit,
      action: EventAction.clickExit,
      labels: {
        MERCHANT_GROUP_ID: merchantGroupID,
        MENU_GROUP_ID: menuGroupID,
      },
    })
    onClose()
  }, [menuGroupID, onClose])

  const handleSubmitFrom = useCallback(() => {
    validateFields(async (err, values) => {
      if (!err && menuGroupID) {
        const resp: any = await dispatch(
          actionCreator.menu.updateMenuGroup({
            merchantGroupID,
            menuGroupID,
            menuGroupName: values.menuGroupName,
          }),
        )
        const responseCode = resp?.payload?.status
        const errMessage = resp?.error?.response?.data?.message
        const errCode = resp?.error?.response?.status

        let labels = {
          MERCHANT_GROUP_ID: merchantGroupID,
          MENU_GROUP_ID: menuGroupID,
          STATUS: "SUCCESS",
        }

        // Update success, refetch menu group detail
        // and close drawer
        if (responseCode === HTTPStatus.NoContent) {
          dispatch(
            actionCreator.menu.fetchMenuGroupDetail({
              menuGroupID,
              merchantGroupID,
            }),
          )
          onClose()
        }
        // There is menu name too long error from BE,
        // display the error in related form field
        else if (
          errCode === HTTPStatus.BadRequest &&
          getDalErrMessage(errMessage) === menuNameTooLongBEMessage
        ) {
          setFields({
            [menuGroupNameField]: {
              value: values.menuGroupName,
              errors: [
                new Error(
                  textMap.menuNameCharExceeded.replace(
                    "<x>",
                    `${maxLengthMenuGroupName}`,
                  ),
                ),
              ],
            },
          })
        }
        // There is menu already exist error from BE,
        // display the error in related form field
        else if (
          errCode === HTTPStatus.BadRequest &&
          getDalErrMessage(errMessage) === menuNameAlreadyExistBEMessage
        ) {
          setFields({
            [menuGroupNameField]: {
              value: values.menuGroupName,
              errors: [new Error(textMap.menuNameAlreadyExist)],
            },
          })
        }
        // Unknown error
        else if (errCode) {
          notification.error({
            message: globalErrorMessage,
          })
        }

        // Track error
        if (resp?.type?.endsWith("_FAIL")) {
          labels = {
            ...labels,
            STATUS: "FAILURE",
            ...getResponseErrorEventLabels(resp),
          }
        }

        TroyWebTracker.sendEvent({
          category: EventCategory.menuGroupEdit,
          action: EventAction.clickSave,
          labels,
        })
      }
    })
  }, [
    menuGroupID,
    merchantGroupID,
    dispatch,
    globalErrorMessage,
    onClose,
    setFields,
    maxLengthMenuGroupName,
    menuGroupNameField,
    textMap.menuNameAlreadyExist,
    textMap.menuNameCharExceeded,
    validateFields,
  ])

  useEffect(() => {
    if (visible) {
      TroyWebTracker.sendEvent({
        category: EventCategory.menuGroupEdit,
        action: EventAction.pageView,
        labels: {
          MERCHANT_GROUP_ID: merchantGroupID,
          MENU_GROUP_ID: menuGroupID,
        },
      })
    }
  }, [visible])

  // Set menu group name by default when drawer first open
  useEffect(() => {
    if (menuGroupName && !isFieldsTouched()) {
      setFieldsValue({ menuGroupName })
    }
  }, [menuGroupName, visible, setFieldsValue, isFieldsTouched])

  const disableSubmitBtn =
    !getFieldValue(menuGroupNameField) ||
    getFieldValue(menuGroupNameField) === menuGroupName ||
    formHasErrors(getFieldsError())

  return (
    <Fragment>
      <Drawer
        title={<span css={drawerTitleStyle}>{textMap.title}</span>}
        placement="right"
        width={450}
        visible={visible}
        onClose={handleDrawerClose}
        destroyOnClose
        css={menuGroupDrawerStyle}
      >
        <div css={menuGroupDrawerFormContainer}>
          <Form.Item label={textMap.menuGroupNameLabel}>
            {getFieldDecorator(menuGroupNameField, {
              rules: [
                {
                  max: maxLengthMenuGroupName,
                  message: textMap.menuNameCharExceeded.replace(
                    "<x>",
                    `${maxLengthMenuGroupName}`,
                  ),
                },
              ],
            })(<Input data-testid="menu-group-drawer-name-input" />)}
          </Form.Item>
        </div>
        <div css={drawerFooter}>
          <Button
            data-testid="menu-group-drawer-save-button"
            type="primary"
            css={menuGroupDrawerMenuConfirmButton}
            disabled={disableSubmitBtn}
            onClick={handleSubmitFrom}
          >
            {textMap.save}
          </Button>
        </div>
      </Drawer>
    </Fragment>
  )
}
export const MenuGroupDrawer = Form.create<MenuGroupDrawerProps>({})(
  MenuGroupDrawerComponent,
)
